import { z } from 'zod';
import { productKeySchema } from './products.js';
/**
 * Host rules for the domain registry. A host maps to exactly one organization + application (+ portal),
 * and only once ownership is verified. Unknown or unverified hosts resolve to nothing: they must never
 * fall through to another tenant or to a default organization.
 */
export const MAX_HOSTNAME_LENGTH = 253;
const LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
/**
 * Normalizes a bare hostname (no scheme, no port): lowercases, strips one trailing dot, converts
 * internationalized names to punycode through the WHATWG URL parser, and rejects IP literals.
 */
export function normalizeHostname(input) {
    const trimmed = input.trim().toLowerCase().replace(/\.$/, '');
    if (!trimmed)
        return { ok: false, reason: 'empty' };
    if (/[/\\@?#:\s%]/.test(trimmed))
        return { ok: false, reason: 'invalid_characters' };
    let ascii;
    try {
        ascii = new URL(`http://${trimmed}`).hostname;
    }
    catch {
        return { ok: false, reason: 'invalid_characters' };
    }
    if (ascii.length > MAX_HOSTNAME_LENGTH)
        return { ok: false, reason: 'too_long' };
    if (IPV4.test(ascii) || ascii.startsWith('['))
        return { ok: false, reason: 'ip_literal' };
    const labels = ascii.split('.');
    if (labels.length < 2)
        return { ok: false, reason: 'single_label' };
    if (!labels.every((l) => LABEL.test(l)))
        return { ok: false, reason: 'invalid_label' };
    return { ok: true, hostname: ascii };
}
/**
 * Splits an Origin or Host header value into hostname and port. Ports are only meaningful for local
 * development hosts (*.localhost); production registry entries never carry one.
 */
export function parseOriginHost(origin) {
    try {
        const url = new URL(origin);
        if (url.protocol !== 'https:' && url.protocol !== 'http:')
            return null;
        return { hostname: url.hostname.toLowerCase(), port: url.port || null };
    }
    catch {
        return null;
    }
}
export const DOMAIN_KINDS = ['employee-app', 'customer-portal'];
export const domainKindSchema = z.enum(DOMAIN_KINDS);
export const DOMAIN_VERIFICATION_STATES = ['pending', 'verified', 'failed'];
export const DOMAIN_TLS_STATES = ['unknown', 'pending', 'ready', 'failed'];
export const DOMAIN_LIFECYCLE_STATES = ['pending', 'active', 'disabled', 'deprovisioning', 'released'];
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
/**
 * Customer branding shown on sign-in pages. Plain values only: no HTML, no scripts, no arbitrary CSS.
 * Logos are https URLs on the platform asset store or relative platform asset paths.
 */
export const brandingSchema = z.object({
    displayName: z.string().min(1).max(80),
    logoUrl: z
        .string()
        .max(500)
        .refine((v) => v.startsWith('https://') || v.startsWith('/brand-assets/'), 'logo must be https or a platform asset path')
        .optional(),
    accentColor: z.string().regex(HEX_COLOR).optional(),
});
/** Picks black or white text for a background color using WCAG relative luminance. */
export function readableTextColor(hex) {
    if (!HEX_COLOR.test(hex))
        return '#000000';
    const channel = (i) => {
        const c = parseInt(hex.slice(i, i + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
    // Contrast against white is (1.05)/(L+0.05); against black (L+0.05)/0.05. Pick the larger.
    return 1.05 / (luminance + 0.05) >= (luminance + 0.05) / 0.05 ? '#ffffff' : '#000000';
}
/** GET /v1/domains/resolve response for a verified, active host. Public data only. */
export const domainResolutionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('trace'),
        host: z.string(),
        applicationKey: z.union([productKeySchema, z.literal('hub')]),
    }),
    z.object({
        kind: z.literal('employee-app'),
        host: z.string(),
        applicationKey: productKeySchema,
        organization: z.object({ id: z.uuid(), branding: brandingSchema }),
    }),
    z.object({
        kind: z.literal('customer-portal'),
        host: z.string(),
        applicationKey: z.literal('portal'),
        organization: z.object({ id: z.uuid(), branding: brandingSchema }),
        portal: z.object({ id: z.uuid(), displayName: z.string(), productKey: productKeySchema }),
    }),
]);
//# sourceMappingURL=domains.js.map